import { useMemo, useState } from 'react';
import { Button } from '@launchpad-ui/components';

import styles from './JsonPayload.module.css';

const TOOL_MARKER_RE = /^\[TOOL (?:INPUT|RESULT):[^\]]*\]\s*/;

/** Payloads longer than this start collapsed behind a toggle, matching the agent view's output cap. */
const COLLAPSED_LINE_COUNT = 10;

/** Drop the `[TOOL INPUT: …]` / `[TOOL RESULT: …]` prefix some instrumentations put on tool payloads. */
export const stripToolMarker = (raw: string): string => raw.replace(TOOL_MARKER_RE, '');

/** JSON object or array; the only values `parsePayloadJson` returns on success. */
export type PayloadJson = { [key: string]: unknown } | unknown[];

const isJsonObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Parse a tool args/result payload or prompt block; undefined when it isn't a JSON object or array. */
export const parsePayloadJson = (raw: string | undefined): PayloadJson | undefined => {
  if (!raw) {
    return undefined;
  }
  const trimmed = stripToolMarker(raw).trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed) || isJsonObject(parsed)) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

/** Pretty-print the payload when it parses as JSON; otherwise show the text as-is. */
export const formatPayload = (raw: string): string => {
  const parsed = parsePayloadJson(raw);
  return parsed === undefined ? raw.trim() : JSON.stringify(parsed, null, 2);
};

/**
 * Code block for a tool argument/result or a JSON block lifted out of a prompt. Same pretty-printed
 * `<pre>` the Vega chat uses for tool inputs and results, so every conversation surface reads the same.
 */
export const JsonPayload: React.FC<{ raw: string }> = ({ raw }) => {
  const [expanded, setExpanded] = useState(false);
  const text = useMemo(() => formatPayload(raw), [raw]);
  const lineCount = useMemo(() => text.split('\n').length, [text]);
  const collapsible = lineCount > COLLAPSED_LINE_COUNT;
  const collapsed = collapsible && !expanded;

  return (
    <div className={styles.payload}>
      <pre
        className={collapsed ? `${styles.code} ${styles.codeCollapsed}` : styles.code}
        data-collapsed={collapsible ? String(collapsed) : undefined}
      >
        {text}
      </pre>
      {collapsible && (
        <Button
          className={styles.toggle}
          size="small"
          variant="minimal"
          aria-expanded={!collapsed}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          {collapsed ? `Show all ${lineCount} lines` : 'Show less'}
        </Button>
      )}
    </div>
  );
};
